import { useNavigate } from "@tanstack/react-router";
import { useT } from "../../i18n";
import type { Uebersetzer } from "../../i18n";
import { useSitzung } from "../../schale/Sitzung";
import { Abschnitt, Zeilenblatt, Zweizeiler } from "../../bausteine/Handyformen";
import { tageSeit } from "../../daten/ortsbegehung";
import type { Aufgabe } from "../../daten/vorbereitung";
import type { Aufnahmetermin } from "../../daten/typen";
import { terminKurz } from "../../bausteine/Aufnahmetermin";
import "../../stile/handy.css";
import "../../stile/telefon/aufnahmen.css";

/* ══════════════════════════════════════════════════════════════════
   Die offene Ortsaufnahme als Aufgabe — auf „Heute" und dem Start.

   Luca, 19.09.2026: *„Das mit den Ortsaufnahmen ist nicht gut gelöst, und
   vor allem: Wenn wir eine offene Ortsaufnahme haben, dann muss die Aufgabe
   ja schön auf dem Handy auch auftauchen."*

   **Sie tauchte nicht auf, obwohl sie da war.** Die Tagesmappe zeigte seit
   dem 18.09.2026 nur die **eigenen** — und eine frisch bestellte Aufnahme
   gehört niemandem: Sie liegt im Pool, bis jemand sie nimmt
   (19-ortsaufnahme.md, *„Der Pool ist die Zuteilung"*). Genau die Aufnahme,
   um die es geht, stand also auf keinem Telefon. Auf dem Start stand sie
   zwar unter „Was ist zu tun?", aber als Zeile eines Einsatzes: Vorgangs-
   nummer groß, der Kunde zweimal, die Einheit nirgends, und das Antippen
   führte in die Liste statt in die Begehung.

   ```
   ORTSAUFNAHMEN                        1 frei   alle ›
   ┌────────────────────────────────────────────────┐
   │ ○ Kranbau Ostfriesland GmbH             m²   › │
   │   Solarpark Emsland Feld B · Garrel · heute …  │
   └────────────────────────────────────────────────┘
   ```

   **Was hier steht: meine und die freien.** Wer eine hat, sieht sie
   (gefüllter grüner Punkt); was niemand hat, sieht jeder, der sie nehmen
   darf (offener Ring in abgedunkeltem Gelb) — dieselbe Unterscheidung wie in
   der Liste (`vorgaenge/AufnahmenTelefon.tsx`). Eine Aufnahme, die ein
   Kollege genommen hat, ist für mich keine Aufgabe und steht hier nicht.

   **Die Zeile führt in die Begehung**, nicht in die Liste. Liegt sie im
   Pool, ist dort „Ich mache das" die Hauptaktion — ein Griff mehr als ein
   Knopf in der Zeile, aber ein Knopf in einem Knopf ist kein gültiges HTML,
   und wer sie nimmt, will ohnehin sehen, wohin er fährt.

   **Keine zweite Abfrage-Wahrheit.** Die Zeilen kommen, wo die Maske ohnehin
   fragt: auf „Heute" aus `GET /handy/heute`, auf dem Start aus `GET /start`
   — beide lesen sie an **einer** Stelle (`dienste/aufgaben.ts`,
   `aufnahmenLesen`), und dort steht auch, wer was sieht. Die Mappe nimmt
   sie damit aufs Gerät mit: Im Funkloch stehen sie trotzdem da (0113).
   ══════════════════════════════════════════════════════════════════ */

/** Wie viele Zeilen der Abschnitt zeigt, bevor „alle" auf die Liste verweist. */
const AUSZUG = 4;

/** „heute bestellt" · „seit gestern offen" · „seit 3 Tagen offen". */
export function seitText(t: Uebersetzer, iso: string): string {
  const tage = tageSeit(iso);
  if (tage === null) return "";
  if (tage <= 0) return t("ortsbegehung.seitHeute");
  if (tage === 1) return t("ortsbegehung.seitGestern");
  return t("ortsbegehung.seitTagen", { zahl: tage });
}

/**
 * Was eine Aufnahmezeile unter und neben dem Kunden trägt — überall gleich
 * (Liste, Heute, Start).
 *
 * **Unter dem Kunden: wo, dann seit wann** — ein Merkmal, einzeilig. Der Ort
 * steht vorn, weil er entscheidet, ob die Fahrt heute passt; das Alter hinten,
 * weil es nur einordnet — wird abgeschnitten, dann dort.
 *
 * **Rechts: was gemessen wird** — „m²", „Module". Es ist die Aufgabe selbst
 * und kurz, und rechts wird es nie abgeschnitten. Am 18.09.2026 stand es als
 * „Aufmaß in m²" am Ende der leisen Zeile und fiel auf 390 px als Erstes weg;
 * ein Versuch am 19.09.2026 ließ die Zeile dafür zweizeilig umbrechen — und
 * dann waren die Einträge verschieden hoch. Luca am selben Tag: *„… dass diese
 * Einträge in der Liste immer gleich hoch sind, damit es ein konsistentes
 * Design einfach ist."* Also: 56 px, zwei Zeilen, die Einheit rechts.
 */
export function aufnahmeZeile(t: Uebersetzer, a: {
  ort: string; ortschaft: string; einheit: string; bestelltIso: string;
  termin?: Aufnahmetermin | null;
}): { unter: string; rechts: string } {
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  return {
    unter: [
      [a.ort, a.ortschaft].filter(Boolean).join(trenn) || t("ortsbegehung.ohneOrt"),
      /* **Mit Termin steht der Termin da, nicht das Alter** (0180): „seit
         3 Tagen offen" liest sich wie liegengeblieben, und eine verabredete
         Fahrt ist das Gegenteil davon. */
      a.termin ? terminKurz(t, a.termin) : seitText(t, a.bestelltIso),
    ].filter(Boolean).join(trenn),
    rechts: a.einheit,
  };
}

/**
 * Welche offenen Aufnahmen ein Mensch als **seine Aufgabe** sieht: die, die
 * er hat, und die, die er nehmen darf — seine zuerst. Eine Regel, an einer
 * Stelle, für beide Schirme; wer was **sehen** darf, hat der Server schon
 * entschieden.
 */
export function aufnahmenFuerMich(aufgaben: readonly Aufgabe[] | undefined): Aufgabe[] {
  const offen = (aufgaben ?? []).filter((a) => a.art === "ortsaufnahme" && a.aufnahmeId);
  return [
    ...offen.filter((a) => a.meins),
    ...offen.filter((a) => !a.meins && a.darfNehmen),
  ];
}

/**
 * Der Abschnitt „Ortsaufnahmen" — **fehlt ganz, wenn keine ansteht**, wie
 * die Abschnitte um ihn herum (auf „Heute" die Einsätze, auf dem Start die
 * Wiedervorlage). Ein Leersatz stünde hier auf jedem Telefon ohne
 * Außendienst — und sagte etwas, wonach niemand gefragt hat.
 */
export function Aufnahmeaufgaben({ aufgaben }: { aufgaben: readonly Aufgabe[] | undefined }) {
  const t = useT();
  const gehZu = useNavigate();
  const { darfAuf } = useSitzung();
  /* Die Begehung ist ein Weg mit Recht (`aufnahmen`). Wer es nicht hat,
     bekäme eine Zeile, deren Ziel ihn abweist. */
  if (!darfAuf("/aufnahmen")) return null;
  const alle = aufnahmenFuerMich(aufgaben);
  if (!alle.length) return null;

  const zeilen = alle.slice(0, AUSZUG);
  const meine = alle.filter((a) => a.meins).length;
  const frei = alle.length - meine;
  /* **Die Auskunft sagt, wem sie gehören** — „1 bei dir · 2 frei". Ist die
     Liste gekürzt, gewinnt die andere Auskunft („4 von 7"): Dann ist die
     wichtigere Aussage, dass nicht alles dasteht. */
  const auskunft = alle.length > zeilen.length
    ? t("handyBlock.vonZahl", { zeigt: zeilen.length, alle: alle.length })
    : [
      meine ? t("handyBlock.beiDir", { zahl: meine }) : "",
      frei ? t("handyBlock.frei", { zahl: frei }) : "",
    ].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `);

  return (
    <Abschnitt
      titel={t("mappe.aufnahmen")}
      auskunft={auskunft}
      /* „alle" steht immer da: Die Liste ist sonst nur über „Mehr" zu
         erreichen, und dort liegt auch, was erledigt ist. */
      weg="/aufnahmen"
      wegWort={t("handyLeiste.alle")}
    >
      <Zeilenblatt>
        {zeilen.map((a) => (
          <Zweizeiler
            key={a.id}
            beiKlick={() => void gehZu({ to: "/aufnahmen/$id", params: { id: a.aufnahmeId ?? "" } })}
            punkt={a.meins ? "gut" : "acht"}
            titel={a.kunde}
            {...aufnahmeZeile(t, {
              ort: a.ort,
              ortschaft: a.ortschaft,
              einheit: a.messung?.einheit ?? "",
              bestelltIso: a.angelegtIso ?? a.faelligIso ?? "",
              termin: a.termin,
            })}
          />
        ))}
      </Zeilenblatt>
    </Abschnitt>
  );
}
